import { useCallback, useEffect, useMemo, useState } from "react";

import {
    ALL_MINTS,
    IS_LOCAL_NX_DEV,
    NullableRecord,
    TIME,
    filterNullableRecord,
    firstElement,
    greaterThan,
    lamportsToUiAmount,
    roundDownToDecimals,
    uiAmountToLamports
} from "@bridgesplit/utils";
import { TokenListMetadata, StrategyDuration, UncertaintyType, OracleQuote } from "@bridgesplit/abf-sdk";
import { skipToken } from "@reduxjs/toolkit/dist/query";
// eslint-disable-next-line
import { useMemoizedKeyMap, useMemoizedMap } from "@bridgesplit/ui";

import {
    BsMetaUtil,
    getAssetIdentifier,
    getAssetMint,
    getAssetType,
    useAbfTypesToUiConverter,
    getAssetData
} from "../utils";
import {
    MarketExpanded,
    MaxQuoteFromCollateralAmount,
    MarketDetailStats,
    BestQuote,
    Collateral,
    CollateralWithMaxQuote,
    AbfLoanExpanded,
    PrincipalMarketStats,
    BorrowCap,
    CollateralWithTerms,
    MAX_LOANS_PER_SELL_QUOTE_BATCH,
    MultiSellLoanQuoteEntry,
    QuoteCollateralInfo,
    SellLoanQuote,
    PriceFetchType,
    CollateralFilter,
    LoanCollateralExpanded,
    ParsedMarketInformationWithLtvInfo,
    MarketInformationAssetData,
    MarketInformationsFilter,
    MarketAssetView,
    StrategyExpandedInfo,
    PrincipalCollateralBorrowRateQuery,
    PrincipalCollateralRateHistorySortType
} from "../types";
import {
    useBestQuotesQuery,
    useMarketQuotesQuery,
    useStatsQuery,
    useMaxQuotesQuery,
    usePresetPrincipalByMintsQuery,
    useStrategyDurationsQuery,
    useSellQuoteQuery,
    useSellQuoteBatchQuery,
    useLoopCollateralYieldLatestByMintsQuery,
    usePrincipalCollateralRateHistoryQuery,
    useOracleMarketInformationsQuery
} from "../reducers";
import { useOraclePrices } from "./pricing";
import { useExternalYieldVaults, usePausedExternalYieldSources } from "./external-yield";
import { getZcCollateral, getZcLedger, isClmmCollateral } from "./loans";
import { isWhirlpoolMetadata } from "./whirlpool";
import { isRaydiumMetadata } from "./raydium";
import { getClmmPositionMint } from "./clmm";
import { useOracleMarketInformationsCacheQuery } from "../cache";
import { useTokenListContext } from "./token-list";

// Shared superset stats query. Other hooks (e.g. useLoopsExpanded) reuse this exact object so their
// useStatsQuery args stringify identically and dedupe to a single /stats request per page.
export const ALL_MARKETS_STATS_QUERY_PARAMS = {
    principalMints: ALL_MINTS,
    includeStrategy: true,
    includePrincipal: true,
    includeBorrowCaps: true,
    includeCollateral: true,
    includeEarn: true,
    includeTotals: true
};

export function useMarkets(options?: { skip?: boolean }) {
    const { principalTokens, principalTokenMints } = useTokenListContext();

    const { data: statsQuery, isLoading: statsLoading } = useStatsQuery(ALL_MARKETS_STATS_QUERY_PARAMS, {
        skip: options?.skip
    });

    const { getOracle } = useOraclePrices(principalTokenMints, PriceFetchType.Twap);

    const { convertPrincipalMarketStats, convertStrategyStats } = useAbfTypesToUiConverter();
    const principalStatsMap = useMemo(
        () => (statsQuery?.principal ? new Map(Object.entries(statsQuery.principal)) : undefined),
        [statsQuery]
    );
    const strategyMap = useMemo(
        () => (statsQuery?.strategy ? new Map(Object.entries(statsQuery.strategy)) : undefined),
        [statsQuery]
    );
    const borrowCapsMap = useMemo(
        () => (statsQuery?.borrowCaps ? new Map(Object.entries(statsQuery.borrowCaps)) : undefined),
        [statsQuery]
    );

    const collateralDepositsMap = useMemo(
        () => (statsQuery?.collateral ? new Map(Object.entries(statsQuery.collateral)) : undefined),
        [statsQuery]
    );

    const { data: collateralYields, isLoading: collateralYieldLoading } =
        useLoopCollateralYieldLatestByMintsQuery(ALL_MINTS);

    const { getYieldVault, isLoading: yieldVaultLoading } = useExternalYieldVaults();

    const isLoading = statsLoading || !principalTokens || yieldVaultLoading || collateralYieldLoading;

    const data = principalTokens
        ?.map((metadata): NullableRecord<MarketExpanded> => {
            const usdPrice = getOracle(metadata.mint, PriceFetchType.Twap)?.usdPrice ?? null;
            const strategyStats = strategyMap?.get(metadata.mint) ?? { totalDeposits: 0, durationToMinApy: [] };

            // api will omit mints without loans but these shouldn't be filtered out
            const principalStats: PrincipalMarketStats = principalStatsMap?.get(metadata.mint) ?? {
                principalOriginated: 0,
                principalUtilized: 0,
                principalPrice: 0,
                wAvgBorrowApy: null,
                wAvgLendApy: null,
                durationToWAvgBorrowApy: []
            };
            const borrowCap = borrowCapsMap?.get(metadata.mint) ?? null;
            const collateralDepositsUsd = collateralDepositsMap?.get(metadata.mint) ?? 0;

            return {
                strategyStats: strategyStats ? convertStrategyStats(metadata.mint, strategyStats) : undefined,
                principalStats: convertPrincipalMarketStats(metadata.mint, principalStats),
                collateralDepositsUsd,
                borrowCap,
                metadata,
                usdPrice,
                yieldVault: getYieldVault(metadata.mint),
                collateralYield: collateralYields?.[metadata.mint] ?? null
            };
        })
        .filter(filterNullableRecord)
        .sort(
            (a, b) =>
                (b.usdPrice ?? 0) * (b.strategyStats.totalDeposits + b.principalStats.principalOriginated) -
                (a.usdPrice ?? 0) * (a.strategyStats.totalDeposits + a.principalStats.principalOriginated)
        );

    return {
        data: isLoading ? undefined : data,
        earnStats: statsQuery?.earn,
        platformTotals: statsQuery?.totals,
        isLoading
    };
}

export function useMarketStats(mint: string | undefined, options?: { skip?: boolean }) {
    const { data: stats, isLoading: statsLoading } = useStatsQuery(
        {
            principalMints: mint ? [mint] : [],
            includeStrategy: true,
            includePrincipal: true,
            includePrincipalToCollateralDepositedUsd: true,
            includeBorrowCaps: true,
            force: true
        },
        { skip: !mint || options?.skip }
    );
    const strategyStats = mint ? stats?.strategy?.[mint] : undefined;
    const principalStats = mint ? stats?.principal?.[mint] : undefined;
    const principalToCollateralDepositedUsd = mint ? stats?.principalToDepositedCollateralUsd?.[mint] : undefined;
    const borrowCap = mint ? stats?.borrowCaps?.[mint] : undefined;

    // pass empty array since we already have metadata
    const { convertPrincipalMarketStats, convertStrategyStats } = useAbfTypesToUiConverter();

    const isLoading = statsLoading;
    const data = useMemo((): MarketDetailStats | undefined => {
        if (!strategyStats || !principalStats || !mint || isLoading || !borrowCap) return undefined;
        return {
            strategyStats: convertStrategyStats(mint, strategyStats),
            principalStats: convertPrincipalMarketStats(mint, principalStats),
            principalToCollateralDepositedUsd,
            borrowCap
        };
    }, [
        convertPrincipalMarketStats,
        convertStrategyStats,
        isLoading,
        mint,
        principalStats,
        strategyStats,
        principalToCollateralDepositedUsd,
        borrowCap
    ]);

    return { data, isLoading };
}

export function useMinPrincipalDeposit(principalMetadata: TokenListMetadata | undefined) {
    const { data: rawData } = usePresetPrincipalByMintsQuery(principalMetadata ? [principalMetadata.mint] : skipToken, {
        skip: !principalMetadata?.mint
    });
    const lamportAmount = principalMetadata ? rawData?.[principalMetadata?.mint] : undefined;
    const uiAmount = lamportAmount ? lamportsToUiAmount(lamportAmount, principalMetadata?.decimals) : undefined;
    return { uiAmount, lamportAmount };
}

export function useSupportedCollateral(
    principalMint: string | undefined,
    options?: {
        skip?: boolean;
        includePrincipalMint?: boolean;
        requireMarketInformation?: boolean;
        includeWalletOnly?: boolean;
    }
) {
    const { collateralTokens, allCollateralTokens } = useTokenListContext();
    const { getMarketByCollateralAndPrincipalMint } = usePresetMarketInfos({
        principalMints: principalMint ? [principalMint] : [],
        addresses: []
    });

    const tokens = options?.includeWalletOnly ? allCollateralTokens : collateralTokens;

    return tokens?.filter((t) => {
        const hasCollateral = options?.includePrincipalMint ? true : t.mint !== principalMint;
        if (!hasCollateral) return false;
        if (options?.requireMarketInformation) {
            return !!getMarketByCollateralAndPrincipalMint(t.mint, principalMint);
        }
        return true;
    });
}

/**
 * Helper function to find the "Default" market info (where metadata is empty or undefined)
 */
export function getDefaultMarketInfo(
    marketInfos: ParsedMarketInformationWithLtvInfo[] | undefined
): ParsedMarketInformationWithLtvInfo | undefined {
    if (!marketInfos || marketInfos.length === 0) return undefined;
    // Find market info with no metadata or empty metadata array
    const defaultInfo = marketInfos?.find((mi) => mi.marketInformation.isDefault);
    return defaultInfo;
}

export function usePresetMarketInfos({ principalMints, addresses }: MarketInformationsFilter) {
    // The eager app-load prefetch is slim (no asset info), so fetch full asset info scoped to this
    // request. The response merges into the cache; the cache hook below reads it back. Skip when
    // unscoped to avoid re-fetching every market's asset info eagerly.
    const { isLoading: queryLoading, isFetching: queryFetching } = useOracleMarketInformationsQuery(
        { verified: true, principalMints, addresses },
        { skip: !principalMints?.length && !addresses?.length }
    );

    const {
        allMarkets,
        isLoading: cacheLoading,
        isFetching: cacheFetching
    } = useOracleMarketInformationsCacheQuery({
        verified: true,
        principalMints,
        addresses
    });

    // The cache holds slim entries (no asset info) until the scoped fetch above lands, so OR in its
    // loading state — otherwise consumers would briefly read empty asset info as if it were ready.
    const isLoading = cacheLoading || queryLoading;
    const isFetching = cacheFetching || queryFetching;

    const principalMintsToRawValues = useMemoizedKeyMap(allMarkets, (m) => m.marketInformation.principalMint);

    const principalToCollateralMintToMarketInfoMap = useMemo(() => {
        if (!principalMintsToRawValues) return undefined;

        const map = new Map<string, Map<string, MarketAssetView[]>>();

        for (const [principalMint, markets] of principalMintsToRawValues.entries()) {
            const collateralMap = map.get(principalMint) ?? new Map<string, MarketAssetView[]>();

            for (const asset of markets.marketInformationAssetInfo) {
                const existing = collateralMap.get(asset.assetIdentifier) ?? [];

                existing.push({
                    market: markets.marketInformation,
                    asset,
                    customOracles: getCustomOraclesForAsset(markets, asset)
                });
                collateralMap.set(asset.assetIdentifier, existing);
            }

            map.set(principalMint, collateralMap);
        }

        return map;
    }, [principalMintsToRawValues]);

    const getDefaultMarketInfoForPrincipal = useCallback(
        (principalMint: string | undefined) => {
            if (!allMarkets) return undefined;
            const principalMarkets = allMarkets?.filter((m) => m.marketInformation.principalMint === principalMint);
            return getDefaultMarketInfo(principalMarkets);
        },
        [allMarkets]
    );

    const getMarketByCollateralAndPrincipalMint = useCallback(
        (collateralMint: string | undefined, principalMint: string | undefined, marketInfoAddress?: string) => {
            if (!collateralMint || !principalMint || !principalToCollateralMintToMarketInfoMap) return undefined;

            const infos = principalToCollateralMintToMarketInfoMap.get(principalMint)?.get(collateralMint);

            if (!infos || infos.length === 0) return undefined;

            if (marketInfoAddress) {
                return infos.find((info) => info.market.address === marketInfoAddress);
            }

            return infos.find((info) => info.market.isDefault);
        },
        [principalToCollateralMintToMarketInfoMap]
    );

    const getPrincipalMarketInfo = useCallback(
        (principalMint: string | undefined, marketInfoAddress?: string) => {
            if (!principalMint || !principalToCollateralMintToMarketInfoMap) return undefined;
            const infos = principalToCollateralMintToMarketInfoMap.get(principalMint)?.get(principalMint);
            if (!infos || infos.length === 0) return undefined;

            // If market info address is specified, return that specific one
            if (marketInfoAddress) {
                return infos.find((info) => info.market.address === marketInfoAddress);
            }

            // When no address specified, ONLY return the "Default" market info (no metadata or empty metadata array)
            // Do NOT fall back to other market infos - return undefined if there's no default
            return infos.find((info) => info.market.isDefault);
        },
        [principalToCollateralMintToMarketInfoMap]
    );

    const getAllMarketInfosByCollateralAndPrincipal = useCallback(
        (collateralMint: string | undefined, principalMint: string | undefined) => {
            if (!collateralMint || !principalMint || !principalToCollateralMintToMarketInfoMap) return undefined;

            return principalToCollateralMintToMarketInfoMap.get(principalMint)?.get(collateralMint);
        },
        [principalToCollateralMintToMarketInfoMap]
    );

    return {
        data: allMarkets,
        getMarketByCollateralAndPrincipalMint,
        getAllMarketInfosByCollateralAndPrincipal,
        getPrincipalMarketInfo,
        getDefaultMarketInfoForPrincipal,
        isLoading,
        isFetching
    };
}

export function useMarketOracleInfos({ filter }: { filter: MarketInformationsFilter }) {
    // The eager app-load prefetch is slim (no asset info), so fetch full asset info scoped to this
    // request. The response merges into the cache; the cache hook below reads it back. Skip when
    // unscoped to avoid re-fetching every market's asset info eagerly.
    const { isLoading: queryLoading, isFetching: queryFetching } = useOracleMarketInformationsQuery(
        { verified: filter.verified, principalMints: filter.principalMints, addresses: filter.addresses },
        { skip: !filter.principalMints?.length && !filter.addresses?.length }
    );

    const {
        data: marketInformation,
        allMarkets,
        isLoading: cacheLoading,
        isFetching: cacheFetching
    } = useOracleMarketInformationsCacheQuery({
        verified: filter.verified,
        principalMints: filter.principalMints,
        addresses: filter.addresses
    });

    // The cache holds slim entries (no asset info) until the scoped fetch above lands, so OR in its
    // loading state — otherwise consumers would briefly read empty asset info as if it were ready.
    const isLoading = cacheLoading || queryLoading;
    const isFetching = cacheFetching || queryFetching;

    const getMarketInformationByAddressAndMints = useCallback(
        (
            marketAddress: string | undefined,
            collateralMint: string | undefined,
            principalMint: string | undefined
        ): MarketAssetView | undefined => {
            if (!marketInformation || !marketAddress || !collateralMint || !principalMint) return undefined;

            // Find the matching ParsedMarketInformationWithLtvInfo
            const matchingMarketInformation = allMarkets?.find(
                (info) =>
                    info.marketInformation.address === marketAddress &&
                    info.marketInformation.principalMint === principalMint
            );
            if (!matchingMarketInformation) return undefined;

            // Find the asset info for the provided collateralMint
            const asset = matchingMarketInformation.marketInformationAssetInfo.find(
                (info) => info.assetIdentifier === collateralMint
            );
            if (!asset) return undefined;

            return {
                market: matchingMarketInformation.marketInformation,
                asset,
                customOracles: getCustomOraclesForAsset(matchingMarketInformation, asset)
            };
        },
        [marketInformation, allMarkets]
    );

    const getMarketInformationsByCollateralMint = useCallback(
        (collateralMint: string | undefined): ParsedMarketInformationWithLtvInfo[] | undefined => {
            if (!collateralMint || !marketInformation) return undefined;
            // Return all market infos which have this collateralMint in their asset info

            return allMarkets?.filter((info) =>
                info.marketInformationAssetInfo.some((asset) => asset.assetIdentifier === collateralMint)
            );
        },
        [allMarkets, marketInformation]
    );

    const getMarketInformationByPrincipalMint = useCallback(
        (principalMint: string | undefined): ParsedMarketInformationWithLtvInfo[] | undefined => {
            if (!principalMint || !marketInformation) return undefined;
            return allMarkets?.filter((info) => info.marketInformation.principalMint === principalMint);
        },
        [allMarkets, marketInformation]
    );

    return {
        data: allMarkets,
        getMarketInformationByAddressAndMints,
        getMarketInformationsByCollateralMint,
        getMarketInformationByPrincipalMint,
        isLoading,
        isFetching
    };
}

export function useCollateralWithTerms({
    principalMint,
    collateral,
    selectedMarketInfoAddress,
    marketInformationAddresses,
    existingStrategy,
    skip
}: {
    principalMint: string | undefined;
    collateral: TokenListMetadata[] | undefined;
    selectedMarketInfoAddress?: string;
    marketInformationAddresses?: string[];
    existingStrategy?: StrategyExpandedInfo;
    skip?: boolean;
}) {
    const { getMarketInformationByAddressAndMints, isLoading } = useMarketOracleInfos({
        filter: {
            principalMints: principalMint ? [principalMint] : [],
            addresses: marketInformationAddresses ?? []
        }
    });

    const { getAllMarketInfosByCollateralAndPrincipal } = usePresetMarketInfos({
        principalMints: principalMint ? [principalMint] : [],
        addresses: []
    });

    // Vaults with neither a selection nor addresses fetch from ALL market infos
    const marketInfoAddress = selectedMarketInfoAddress ?? firstElement(marketInformationAddresses);

    const data = useMemo(() => {
        return collateral
            ?.map((metadata): NullableRecord<CollateralWithTerms> => {
                if (existingStrategy) {
                    // Find the matching collateral in the strategy
                    const existingCollateral = existingStrategy?.collateral?.find(
                        (c) => c.metadata.mint === metadata.mint
                    );

                    if (existingCollateral) {
                        return {
                            metadata,
                            ltvInfo: existingCollateral.ltvInfo
                        };
                    }
                }

                if (marketInfoAddress) {
                    const allLtvInfos = getMarketInformationByAddressAndMints(
                        marketInfoAddress,
                        metadata.mint,
                        principalMint
                    );

                    return {
                        metadata,
                        ltvInfo: allLtvInfos?.asset
                    };
                }

                // When no marketInfoAddress (vault mode), get ALL market infos and pick the one with highest LTV
                const allMarketInfos = getAllMarketInfosByCollateralAndPrincipal(metadata.mint, principalMint);
                if (allMarketInfos && allMarketInfos.length > 0) {
                    // Pick the market info with the highest LTV
                    const bestMarketInformationAssetInfo = getMarketInformationAssetInfoWithHighestLtv(allMarketInfos);
                    return {
                        metadata,
                        ltvInfo: bestMarketInformationAssetInfo
                    };
                }

                // Fallback if no market infos found
                return {
                    metadata,
                    ltvInfo: undefined
                };
            })
            .filter(filterNullableRecord);
    }, [
        collateral,
        existingStrategy,
        getMarketInformationByAddressAndMints,
        marketInfoAddress,
        principalMint,
        getAllMarketInfosByCollateralAndPrincipal
    ]);

    return { data, isLoading };
}

export function getZcMarketInformation(
    marketInfos: ParsedMarketInformationWithLtvInfo[]
): ParsedMarketInformationWithLtvInfo | undefined {
    if (!marketInfos || marketInfos.length === 0) return undefined;
    return marketInfos[0];
}

export function getZcMarketInformationAssetInfo(
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined
): MarketInformationAssetData | undefined {
    if (!marketInfo) return undefined;
    return marketInfo.marketInformationAssetInfo[0];
}

function getMarketInformationAssetInfoWithHighestLtv(
    marketInfos: MarketAssetView[]
): MarketInformationAssetData | undefined {
    if (!marketInfos || marketInfos.length === 0) return undefined;
    // Pick the market info with the highest LTV
    const bestMarketInfo = marketInfos.reduce((best, curr) => (curr.asset.ltv > best.asset.ltv ? curr : best));
    return bestMarketInfo.asset;
}

type MarketQuoteParams = {
    collateralMints: string[] | undefined;
    principalMint: string | undefined;
    minPrincipalAmountLamports?: number;
    limit?: number;
    offset?: number;
    strategyDuration: StrategyDuration | undefined;
};

function useQuotes(
    {
        collateralMints,
        principalMint,
        minPrincipalAmountLamports,
        limit = 1000,
        offset = 0,
        strategyDuration
    }: MarketQuoteParams,
    options?: { skip?: boolean }
) {
    const { pausedExternalYieldSources } = usePausedExternalYieldSources();

    const commonParams =
        principalMint && strategyDuration
            ? {
                  principal: principalMint,
                  minPrincipalAmount: minPrincipalAmountLamports,
                  duration: strategyDuration.duration,
                  durationType: strategyDuration.durationType,
                  limit,
                  offset,
                  excluded_external_yield: pausedExternalYieldSources
              }
            : undefined;

    const skip = !principalMint || !strategyDuration || options?.skip;
    const marketQuery = useMarketQuotesQuery(
        commonParams && collateralMints
            ? {
                  ...commonParams,
                  collateral: collateralMints
              }
            : skipToken,
        { skip: skip || !collateralMints?.length }
    );

    return marketQuery;
}

export function useOrderbookQuotes(params: MarketQuoteParams) {
    const { principalMint } = params;

    const { data: quotes, isLoading, isFetching } = useQuotes(params);
    const { convertOrderbookQuote } = useAbfTypesToUiConverter();
    const data = useMemo(() => {
        if (!quotes || !principalMint) return undefined;
        return quotes?.map((q) => convertOrderbookQuote(q, principalMint)).flat();
    }, [convertOrderbookQuote, principalMint, quotes]);

    return { data, isLoading, isFetching };
}

export function useStrategyDurations() {
    const { data, isLoading } = useStrategyDurationsQuery();
    return { strategyDurations: data, isLoading };
}

type MaxQuoteParams = {
    strategyDuration: StrategyDuration | undefined;
    principalMint: string | undefined;
    collateralFilter: CollateralFilter[] | undefined;
    skip?: boolean;
    strategyCredit?: Map<string, number>;
    principalDecimals: number;
};
export function useMaxQuotes({
    strategyDuration,
    collateralFilter,
    principalMint,
    skip,
    strategyCredit,
    principalDecimals
}: MaxQuoteParams) {
    const { pausedExternalYieldSources } = usePausedExternalYieldSources();

    return useMaxQuotesQuery(
        strategyDuration && principalMint && collateralFilter
            ? {
                  duration: strategyDuration.duration,
                  durationType: strategyDuration.durationType,
                  principalMint,
                  collateralFilter,
                  strategyCredit: strategyCredit
                      ? Object.fromEntries(
                            Array.from(strategyCredit.entries()).map(([k, v]) => [
                                k,
                                uiAmountToLamports(v, principalDecimals)
                            ])
                        )
                      : undefined,
                  excluded_external_yield: pausedExternalYieldSources
              }
            : skipToken,

        {
            skip: !strategyDuration || !principalMint || !collateralFilter?.length || skip
        }
    );
}

export function useMaxQuotesForCollateral({
    strategyDuration,
    principalMint,
    collateral,
    skip,
    priceFetchType = PriceFetchType.Twap,
    strategyCredit
}: {
    strategyDuration: StrategyDuration | undefined;
    principalMint: string | undefined;
    collateral: Collateral[] | undefined;
    skip?: boolean;
    priceFetchType?: PriceFetchType;
    strategyCredit?: Map<string, number>;
}) {
    const { convertOfferQuote, getDecimals } = useAbfTypesToUiConverter();

    const principalDecimals = getDecimals(principalMint);

    const collateralFilter: CollateralFilter[] = useMemo(() => {
        const filter: CollateralFilter[] = [];
        if (!collateral) return filter;
        for (let i = 0; i < collateral.length; i++) {
            const c = collateral[i];

            const isClmm = isClmmCollateral(c);
            const assetData = getAssetData(
                c.metadata,
                isClmm && c.clmmPosition ? getClmmPositionMint(c.clmmPosition) : c.metadata.mint
            );

            if (assetData !== undefined) {
                filter.push({
                    amount: uiAmountToLamports(c.amount, c.metadata?.decimals ?? 0),
                    assetData
                });
            }
        }
        return filter;
    }, [collateral]);

    const {
        currentData: maxQuotes,
        isLoading: quotesLoading,
        isFetching
    } = useMaxQuotes({ collateralFilter, strategyDuration, principalMint, strategyCredit, principalDecimals, skip });

    const principalPriceUsd = useOraclePrices([principalMint], priceFetchType).getUsdPrice(
        principalMint,
        UncertaintyType.Add
    );
    const isLoading = principalPriceUsd === undefined || quotesLoading;

    const collateralToMaxQuote = useMemoizedKeyMap(maxQuotes, (q) => q.collateralMint);

    const data = useMemo(() => {
        if (!collateral || !principalMint || (!collateralToMaxQuote && collateralFilter?.length)) return undefined;
        return collateral
            .map((asset): CollateralWithMaxQuote => {
                const quoteRaw = collateralToMaxQuote?.get(asset.metadata.mint);
                const quote = quoteRaw ? convertOfferQuote(quoteRaw, principalMint) : quoteRaw;

                const collateralPriceUsd = asset.usdPrice;

                if (!quote) {
                    return { ...asset, maxQuote: undefined };
                }

                const collateralUsd = collateralPriceUsd ? asset.amount * collateralPriceUsd : asset.usdValue;

                if (!principalPriceUsd || !collateralUsd) {
                    return { ...asset, maxQuote: { ...quote, maxBorrow: 0 } };
                }

                // max borrow needs to be rounded down manually since LTV might not be evenly divisible
                const maxBorrowFromQuote = roundDownToDecimals(
                    collateralUsd * (1 / principalPriceUsd) * quote.ltv,
                    principalDecimals
                );

                const maxBorrow = Math.min(quote.principalAvailable, maxBorrowFromQuote);

                const maxQuote: MaxQuoteFromCollateralAmount = { ...quote, maxBorrow };

                return { ...asset, maxQuote };
            })
            .sort((a, b) => {
                const maxBorrowSort = (b.maxQuote?.maxBorrow ?? 0) - (a.maxQuote?.maxBorrow ?? 0);
                if (maxBorrowSort !== 0) {
                    return maxBorrowSort;
                }
                const balanceSort = b.amount - a.amount;
                if (balanceSort !== 0) {
                    return balanceSort;
                }
                return BsMetaUtil.getSymbolUnique(a.metadata).localeCompare(BsMetaUtil.getSymbolUnique(b.metadata));
            });
    }, [
        collateral,
        principalMint,
        collateralToMaxQuote,
        collateralFilter?.length,
        convertOfferQuote,
        principalPriceUsd,
        principalDecimals
    ]);

    return { data, isLoading, isFetching };
}

type BestQuoteParams = {
    collateralMintToInfo: Map<string, QuoteCollateralInfo> | undefined;
    principalMint: string | undefined;
    strategyDurations: StrategyDuration[] | undefined;
    minPrincipalAmountLamports: number | undefined;
    showSelf?: boolean;
    skip?: boolean;
    disablePolling?: boolean;
    refinanceParams?: {
        refinanceStrategy: string;
        principalRepayment: number;
        minLqt: number;
        additionalBorrowAmount?: number;
        ledgerIndex: number;
    };
    // Scopes quotes to a loan so its stored liquidation-threshold floor filters out sub-floor strategies.
    loanAddress?: string;
};
export function useBestQuotes({
    collateralMintToInfo,
    principalMint,
    strategyDurations,
    minPrincipalAmountLamports,
    showSelf,
    skip,
    disablePolling,
    refinanceParams,
    loanAddress
}: BestQuoteParams) {
    const { pausedExternalYieldSources } = usePausedExternalYieldSources();

    const hasRequiredData = strategyDurations?.length && principalMint && collateralMintToInfo?.size;
    const shouldSkip = !hasRequiredData || skip;

    const queryParams = hasRequiredData
        ? {
              durations: strategyDurations,
              collateralToInfo: Object.fromEntries(collateralMintToInfo.entries()),
              principalMint,
              minPrincipalAmount: minPrincipalAmountLamports || 0,
              showSelf,
              refinanceParams,
              loanAddress,
              excluded_external_yield: pausedExternalYieldSources
          }
        : skipToken;

    const { data, isLoading, isFetching } = useBestQuotesQuery(queryParams, {
        skip: shouldSkip,
        pollingInterval: disablePolling || IS_LOCAL_NX_DEV ? undefined : 15_000
    });

    const { convertOfferQuote } = useAbfTypesToUiConverter();

    const quotes = useMemo(() => {
        if (!data || !principalMint) return undefined;
        return data?.map((q) => convertOfferQuote(q, principalMint));
    }, [data, principalMint, convertOfferQuote]);

    return { data: quotes, isLoading, isFetching };
}

function getQuoteCollateralInfo(
    metadata: TokenListMetadata | undefined,
    usdPrice: number | undefined,
    collateralUsdAmount: number
): QuoteCollateralInfo | undefined {
    if (!metadata || !usdPrice) return undefined;

    const assetIdentifier = getAssetIdentifier(metadata);
    const assetMint = getAssetMint(metadata);
    if (!assetIdentifier || !assetMint) return undefined;

    const isClmm = isWhirlpoolMetadata(metadata) || isRaydiumMetadata(metadata);
    const amount = isClmm ? 1 : uiAmountToLamports(collateralUsdAmount / usdPrice, metadata.decimals);

    return { amount, assetType: getAssetType(metadata), assetMint, assetIdentifier };
}

// Amounts come from streaming oracle prices and are part of the quote cache key, so hold the resolved payload
function useQuoteCollateralInfo(collateralMints: string[] | undefined, collateralUsdAmount: number) {
    const { getUsdPrice } = useOraclePrices(collateralMints);
    const { getMetadata } = useTokenListContext();
    const [snapshot, setSnapshot] = useState<{ key: string | undefined; info: Map<string, QuoteCollateralInfo> }>();

    const key = collateralMints?.join(",");
    const info = new Map<string, QuoteCollateralInfo>();

    collateralMints?.forEach((collateralMint) => {
        const collateralInfo = getQuoteCollateralInfo(
            getMetadata(collateralMint),
            getUsdPrice(collateralMint),
            collateralUsdAmount
        );
        if (collateralInfo) info.set(collateralMint, collateralInfo);
    });

    useEffect(() => {
        setSnapshot((prev) => {
            if (prev && prev.key === key && info.size <= prev.info.size) return prev;
            return { key, info };
        });
    }, [key, info]);

    return snapshot?.info;
}

export function useGlobalBestOffers({
    principalMint,
    collateralMints,
    strategyDurations,
    collateralUsdAmount = 100_000,
    skip,
    showSelf
}: {
    principalMint: string | undefined;
    collateralMints: string[] | undefined;
    strategyDurations: StrategyDuration[] | undefined;
    collateralUsdAmount?: number;
    skip?: boolean;
    showSelf?: boolean;
}) {
    const {
        data: minPrincipal,
        isSuccess: minPrincipalLoaded,
        isError: minPrincipalError
    } = usePresetPrincipalByMintsQuery(principalMint ? [principalMint] : skipToken, {
        skip: !principalMint || skip
    });

    const collateralMintToInfo = useQuoteCollateralInfo(collateralMints, collateralUsdAmount);

    const minPrincipalAmountLamports = principalMint ? minPrincipal?.[principalMint] : undefined;
    const minPrincipalPending = !!principalMint && !skip && !minPrincipalLoaded && !minPrincipalError;

    const { data: bestQuotes } = useBestQuotes({
        minPrincipalAmountLamports,
        collateralMintToInfo,
        principalMint,
        strategyDurations,
        showSelf,
        skip: skip || minPrincipalPending,
        disablePolling: true
    });

    const bestQuotesUnique = new Map<string, BestQuote>();

    if (!bestQuotes) return undefined;
    bestQuotes?.forEach((quote) => {
        const key = `${quote.duration}${quote.durationType}`;
        const prev = bestQuotesUnique.get(key);
        if (!prev || quote.apy < prev.apy) {
            bestQuotesUnique.set(key, quote);
        }
    });

    return Array.from(bestQuotesUnique.values());
}

export function useSellQuote(loanExpanded: AbfLoanExpanded | undefined) {
    const { data: rawData, ...query } = useSellQuoteQuery(loanExpanded?.loan.address ?? skipToken, {
        skip: !loanExpanded
    });
    const data = useMemo((): SellLoanQuote | undefined => {
        if (!rawData) return undefined;
        const decimals = loanExpanded?.principalMetadata?.decimals ?? 0;
        return {
            ...rawData,
            fairSalePriceAtQuoteRate: lamportsToUiAmount(rawData.fairSalePriceAtQuoteRate, decimals),
            valueLeft: lamportsToUiAmount(rawData.valueLeft, decimals),
            salePrice: lamportsToUiAmount(rawData.salePrice, decimals)
        };
    }, [loanExpanded?.principalMetadata?.decimals, rawData]);

    return { data, ...query };
}

// Batch sell-loan quote against POST /quote/sell/v2. Greedily quotes each
// loan in input order, threading committed strategy capacity through the
// batch — see PR #1875 in loopscale-apis. Server caps batches at
// MAX_LOANS_PER_SELL_QUOTE_BATCH; the hook skips above that to avoid 4xx.
export function useSellQuoteBatch(loansExpanded: AbfLoanExpanded[] | undefined) {
    const decimalsByLoan = useMemoizedMap(
        loansExpanded,
        (l) => l.loan.address,
        (l) => l.principalMetadata?.decimals ?? 0
    );

    const loanAddresses = useMemo(() => loansExpanded?.map((l) => l.loan.address) ?? [], [loansExpanded]);
    const canQuery = loanAddresses.length > 0 && loanAddresses.length <= MAX_LOANS_PER_SELL_QUOTE_BATCH;

    const { data: rawData, ...query } = useSellQuoteBatchQuery(canQuery ? { loans: loanAddresses } : skipToken, {
        skip: !canQuery
    });

    const data = useMemo((): MultiSellLoanQuoteEntry[] | undefined => {
        if (!rawData) return undefined;
        return rawData.map((entry) => {
            const decimals = decimalsByLoan?.get(entry.loan) ?? 0;
            return {
                ...entry,
                fairSalePriceAtQuoteRate: lamportsToUiAmount(entry.fairSalePriceAtQuoteRate, decimals),
                valueLeft: lamportsToUiAmount(entry.valueLeft, decimals),
                salePrice: lamportsToUiAmount(entry.salePrice, decimals)
            };
        });
    }, [decimalsByLoan, rawData]);

    return { data, ...query };
}

const WARNING_THRESHOLD = 0.98; // 98%
export function getBorrowCapDetails({
    borrowCap,
    token,
    principalUtilized
}: {
    borrowCap: BorrowCap | null;
    token: TokenListMetadata;
    principalUtilized: number;
}) {
    const globalBorrowCap = lamportsToUiAmount(borrowCap?.global || 0, token?.decimals);
    const percentOfCap = principalUtilized / (globalBorrowCap || 1);
    const warning = !!borrowCap && percentOfCap >= WARNING_THRESHOLD;

    const remainingGlobalCapacity = Math.max(globalBorrowCap - principalUtilized, 0);

    const warningMessage = (function warningMessage() {
        if (!warning) return null;
        if (percentOfCap >= 1) return "The market cap for borrows has been reached";
        return `This market only has ${BsMetaUtil.formatAmount(
            token,
            remainingGlobalCapacity
        )} remaining borrow capacity`;
    })();

    function exceedsPerLoanCap(amount: number | undefined) {
        if (!borrowCap) return false;
        return greaterThan(amount, lamportsToUiAmount(borrowCap?.perLoan, token?.decimals));
    }

    function exceedsGlobalCap(amount: number | undefined) {
        if (!remainingGlobalCapacity) return false;
        return greaterThan(amount, remainingGlobalCapacity);
    }

    return { borrowCap, percentOfCap, warningMessage, exceedsPerLoanCap, exceedsGlobalCap, remainingGlobalCapacity };
}

function getOracleMapping(
    mint: string | undefined,
    marketData: ParsedMarketInformationWithLtvInfo | undefined
): MarketInformationAssetData | undefined {
    if (!mint || !marketData) return undefined;
    return marketData.marketInformationAssetInfo.find((m) => mint === m.assetIdentifier);
}

export function useMarketOracleMapping(loanExpanded: AbfLoanExpanded | undefined) {
    const ledger = getZcLedger(loanExpanded);
    const collateral = getZcCollateral(loanExpanded);

    const collateralMint = collateral?.assetIdentifier;
    const { getMarketInformationByPrincipalMint, getMarketInformationsByCollateralMint } = useMarketOracleInfos({
        filter: {
            addresses: ledger?.marketInformation ? [ledger.marketInformation] : [],
            principalMints: ledger?.principalMint ? [ledger.principalMint] : []
        }
    });

    // TODO: Verify this is correct
    const collateralMarketData = useMemo(() => {
        const marketInfos = getMarketInformationsByCollateralMint(collateralMint);
        if (!marketInfos) return undefined;
        return getOracleMapping(collateralMint, marketInfos[0]);
    }, [collateralMint, getMarketInformationsByCollateralMint]);

    const principalMarketData = useMemo(() => {
        const marketInfos = getMarketInformationByPrincipalMint(ledger?.principalMint);
        if (!marketInfos) return undefined;
        return getOracleMapping(ledger?.principalMint, marketInfos[0]);
    }, [ledger?.principalMint, getMarketInformationByPrincipalMint]);

    return {
        collateralMarketData,
        principalMarketData
    };
}

export function parseCollateral(
    rawCollateral: LoanCollateralExpanded,
    getOracle: (mint: string) => OracleQuote | undefined
): Collateral {
    return {
        ...rawCollateral,
        key: rawCollateral.assetIdentifier,
        usdPrice: rawCollateral.usdPrice ?? 0,
        usdValue: rawCollateral.amount * (rawCollateral.usdPrice ?? 0),
        oracle: getOracle(rawCollateral.metadata.mint) ?? null,
        amount: rawCollateral.amount,
        metadata: rawCollateral.metadata
    };
}

export interface DeduplicatedCollateralResult {
    // Array of unique collateral with best (highest LTV) market info selected
    uniqueCollateral: MarketAssetView[];
    // Map from collateral mint to all available market info options
    collateralToMarketInfoOptions: Map<string, MarketAssetView[]>;
}

/**
 * Deduplicate collateral-oracle pairs across multiple market infos based on asset identifier.
 * For duplicate collateral, prefer higher LTV (more favorable to user).
 * Returns both: the unique set (filtered by best LTV) and all options.
 */
export function deduplicateCollateralOraclePairs(
    marketInfos: ParsedMarketInformationWithLtvInfo[]
): DeduplicatedCollateralResult {
    // Map from collateral mint to: ALL ParsedMarketInformationWithLtvInfo that reference it
    const collateralToMarketInfoOptions = new Map<string, MarketAssetView[]>();
    // Map from collateral mint to: best ParsedMarketInformationWithLtvInfo (by LTV)
    const uniquePairs = new Map<string, MarketAssetView>();

    for (const marketInfo of marketInfos) {
        for (const asset of marketInfo.marketInformationAssetInfo) {
            const collateralMint = asset.assetIdentifier;

            const ltvWithMarket: MarketAssetView = {
                market: marketInfo.marketInformation,
                asset,
                customOracles: getCustomOraclesForAsset(marketInfo, asset)
            };

            // Track all market info options for this collateral
            const existingOptions = collateralToMarketInfoOptions.get(collateralMint) || [];
            existingOptions.push(ltvWithMarket);
            collateralToMarketInfoOptions.set(collateralMint, existingOptions);

            // Deduplication logic: keep the one with higher LTV as default
            const currentBest = uniquePairs.get(collateralMint);
            if (!currentBest || asset.ltv > currentBest.asset.ltv) {
                uniquePairs.set(collateralMint, ltvWithMarket);
            }
        }
    }

    return {
        uniqueCollateral: Array.from(uniquePairs.values()),
        collateralToMarketInfoOptions
    };
}

/**
 * Resolved by asset identifier because oracle accounts are not unique within a
 * market: every beam-backed asset shares one beam account.
 */
export function getCustomOraclesForAsset(
    market: ParsedMarketInformationWithLtvInfo,
    asset: MarketInformationAssetData
): MarketInformationAssetData[] {
    const underlyingMints = market.underlyingAssets[asset.assetIdentifier];

    if (!underlyingMints || underlyingMints.length === 0) {
        return [];
    }

    const assetIdentifierToAssetInfo = new Map(
        market.marketInformationAssetInfo.map((info) => [info.assetIdentifier, info])
    );

    return underlyingMints
        .map((mint) => assetIdentifierToAssetInfo.get(mint))
        .filter((assetInfo): assetInfo is MarketInformationAssetData => !!assetInfo);
}

export function usePrincipalCollateralBorrowRate(
    params: Partial<PrincipalCollateralBorrowRateQuery>,
    options?: { skip?: boolean }
) {
    const shouldSkip = options?.skip || !params.principalMint || !params.timeLookback;

    const filter: PrincipalCollateralBorrowRateQuery = {
        principalMint: params.principalMint ?? "",
        collateralMints: params.collateralMints ?? [],
        timeLookback: params.timeLookback,
        limit: params.limit ?? undefined,
        sortType: params.sortType ?? PrincipalCollateralRateHistorySortType.PrincipalOutstanding,
        sortDirection: params.sortDirection ?? undefined,
        sampleIntervalSecs: params.sampleIntervalSecs ?? getDefaultRateSampleInterval(params.timeLookback)
    };

    const { data, isLoading, isFetching } = usePrincipalCollateralRateHistoryQuery(filter, { skip: shouldSkip });

    return { data, isLoading, isFetching };
}

/**
 * Suggested sample interval per lookback window — keeps points per series ~100-200
 * regardless of window length. Returns undefined for sub-day windows (full resolution).
 */
export function getDefaultRateSampleInterval(timeLookback: number | undefined): number | undefined {
    if (!timeLookback) return undefined;
    if (timeLookback <= TIME.DAY) return undefined; // full resolution
    if (timeLookback <= TIME.WEEK) return TIME.HOUR;
    if (timeLookback <= TIME.MONTH) return TIME.HOUR * 4;
    return TIME.DAY;
}
